/**
 * Unified analytics layer for Upscale Nails & Spa.
 *
 * Two parallel sinks, by design:
 *   1. Google (GA4 + Google Ads conversions, optionally via GTM) — loaded only
 *      when the matching REACT_APP_* IDs are present; otherwise a safe no-op.
 *   2. First-party funnel log — always on, POSTed to /api/funnel/event. This is
 *      ad-blocker-proof and lets the owner diagnose the real workflow.
 *
 * Hard rule: analytics is NON-BLOCKING. Every call is wrapped so a failure can
 * never break booking, calling, or campaign flows.
 *
 * IDs are configured in /app/frontend/.env (see ANALYTICS_SETUP.md):
 *   REACT_APP_GA4_MEASUREMENT_ID         e.g. G-XXXXXXXXXX
 *   REACT_APP_GADS_CONVERSION_ID         e.g. AW-XXXXXXXXX
 *   REACT_APP_GADS_LABEL_BOOKING         completed-booking conversion label
 *   REACT_APP_GADS_LABEL_CALL            click-to-call conversion label
 *   REACT_APP_GADS_LABEL_BOOKING_STARTED optional micro-conversion label
 *   REACT_APP_GTM_CONTAINER_ID           e.g. GTM-XXXXXXX
 *   REACT_APP_ANALYTICS_DEBUG            "true" → console debug logging
 *   REACT_APP_PROD_DOMAIN                https://deals.upscaleflowermound.com
 */

import { isMeasurementEnabled, subscribePrivacyChanges, clearMeasurementIdentifiers } from "./privacyChoices";

const env = process.env;
const BACKEND_URL = env.REACT_APP_BACKEND_URL;

export const ANALYTICS_CONFIG = {
  ga4: (env.REACT_APP_GA4_MEASUREMENT_ID || "").trim(),
  adsId: (env.REACT_APP_GADS_CONVERSION_ID || "").trim(),
  labelBooking: (env.REACT_APP_GADS_LABEL_BOOKING || "").trim(),
  labelCall: (env.REACT_APP_GADS_LABEL_CALL || "").trim(),
  labelBookingStarted: (env.REACT_APP_GADS_LABEL_BOOKING_STARTED || "").trim(),
  gtm: (env.REACT_APP_GTM_CONTAINER_ID || "").trim(),
  prodDomain: (env.REACT_APP_PROD_DOMAIN || "").trim(),
  debug: (env.REACT_APP_ANALYTICS_DEBUG || "").toLowerCase() === "true",
};

const isProdHost = () => {
  try {
    if (!ANALYTICS_CONFIG.prodDomain) return false;
    return window.location.origin === ANALYTICS_CONFIG.prodDomain;
  } catch { return false; }
};

const dbg = (...args) => {
  if (ANALYTICS_CONFIG.debug || !isProdHost()) {
    // eslint-disable-next-line no-console
    console.log("%c[analytics]", "color:#00A09B", ...args);
  }
};

/** Which Google layers are configured — drives the owner "setup needed" hint. */
export function getConfigStatus() {
  const c = ANALYTICS_CONFIG;
  return {
    ga4: !!c.ga4,
    ads: !!c.adsId,
    adsBookingLabel: !!c.labelBooking,
    adsCallLabel: !!c.labelCall,
    gtm: !!c.gtm,
    anyGoogle: !!(c.ga4 || c.adsId || c.gtm),
    isProdHost: isProdHost(),
    missing: [
      !c.ga4 && "GA4 Measurement ID (REACT_APP_GA4_MEASUREMENT_ID)",
      !c.adsId && "Google Ads Conversion ID (REACT_APP_GADS_CONVERSION_ID)",
      !c.labelBooking && "Ads booking conversion label (REACT_APP_GADS_LABEL_BOOKING)",
      !c.labelCall && "Ads click-to-call label (REACT_APP_GADS_LABEL_CALL)",
    ].filter(Boolean),
  };
}

// ── gtag bootstrap ────────────────────────────────────────────────────────
function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
}

let _initialized = false;
export function initAnalytics() {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;
  ensureGtagStub();
  const c = ANALYTICS_CONFIG;
  // Respect the customer's Privacy Choice (§4/§7). When measurement is
  // OFF, we STILL initialize the stub (so calls remain non-throwing) but
  // we DO NOT load Google scripts, capture attribution, or heartbeat.
  const measurementOn = isMeasurementEnabled();

  try {
    // Google Tag Manager (if used as the container)
    if (measurementOn && c.gtm) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${c.gtm}`;
      document.head.appendChild(s);
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      dbg("GTM loaded", c.gtm);
    }

    // gtag.js for GA4 + Google Ads (independent of GTM)
    const primaryId = measurementOn ? (c.ga4 || c.adsId) : "";
    if (primaryId) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
      document.head.appendChild(s);
      window.gtag("js", new Date());
      // Manual SPA pageviews — disable gtag's automatic page_view.
      if (c.ga4) window.gtag("config", c.ga4, { send_page_view: false });
      if (c.adsId) window.gtag("config", c.adsId);
      dbg("gtag loaded", { ga4: c.ga4, ads: c.adsId });
    }

    if (!c.ga4 && !c.adsId && !c.gtm) {
      dbg("No Google IDs configured — Google layer is a safe no-op. First-party funnel logging still active.");
    }
    if (!measurementOn) {
      dbg("measurement OFF (customer Privacy Choice) — Google + first-party layers held.");
    }
  } catch (e) {
    dbg("init error (non-fatal)", e);
  }

  if (measurementOn) {
    captureAttribution();
    // P1.4B.2 — one heartbeat per browser session so the server can enforce
    // the owner's governed anonymous-identity lifetime (180-day inactivity,
    // 365-day absolute maximum). Non-blocking. Best-effort.
    try { anonymousJourneyHeartbeat(); } catch { /* noop */ }
  }

  // Subscribe once for the lifetime of the tab: if the customer toggles
  // their preference we clear/refresh the persistent identifiers
  // immediately so the choice is operationally meaningful (§7).
  try {
    subscribePrivacyChanges((state) => {
      if (!state.enabled) {
        clearMeasurementIdentifiers();
      } else {
        // Prospective only — never reconstruct missing history (§8).
        try { captureAttribution(); } catch { /* noop */ }
        try { anonymousJourneyHeartbeat(); } catch { /* noop */ }
      }
    });
  } catch { /* noop */ }
}

// ── attribution capture (first-touch, persisted) ─────────────────────────
const ATTR_KEY = "ua_attribution";
const ATTR_FIELDS = [
  "gclid", "gbraid", "wbraid",
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "landing_variant_id", "campaign_id", "recipient_token",
  // P1.4B.1 · R-B1-03: Google ValueTrack context — whitelist only (R-B1-06).
  // Namespaces stay distinct from Cascade IDs (R-B1-04) via the "google_"
  // prefix. Keyword semantics remain the account/matched keyword Google
  // sent — NOT a verified customer search query (R-B1-05).
  "google_campaignid", "google_adgroupid", "google_creative",
  "google_keyword", "google_targetid", "google_matchtype",
  "google_network", "google_device",
  "google_loc_physical_ms", "google_loc_interest_ms",
];

// URL-parameter aliases: Google's ValueTrack tokens land in the URL as
// `campaignid`, `adgroupid`, `creative`, `keyword`, `targetid`, `matchtype`,
// `network`, `device`, `loc_physical_ms`, `loc_interest_ms`. We map those
// to our namespaced fields at capture time.
const ATTR_URL_ALIASES = {
  google_campaignid: ["google_campaignid", "campaignid"],
  google_adgroupid: ["google_adgroupid", "adgroupid"],
  google_creative: ["google_creative", "creative"],
  google_keyword: ["google_keyword", "keyword"],
  google_targetid: ["google_targetid", "targetid"],
  google_matchtype: ["google_matchtype", "matchtype"],
  google_network: ["google_network", "network"],
  google_device: ["google_device", "device"],
  google_loc_physical_ms: ["google_loc_physical_ms", "loc_physical_ms"],
  google_loc_interest_ms: ["google_loc_interest_ms", "loc_interest_ms"],
};

export function captureAttribution() {
  // Privacy Choice honored (§7 rule 3): while OFF, we do not persist or
  // create attribution state. Return existing (empty) state so callers
  // continue to receive a plain object.
  if (!isMeasurementEnabled()) { return {}; }
  try {
    const url = new URL(window.location.href);
    const p = url.searchParams;
    const existing = getAttribution();
    const incoming = {};
    ATTR_FIELDS.forEach((f) => {
      let v = p.get(f);
      if (!v && ATTR_URL_ALIASES[f]) {
        for (const a of ATTR_URL_ALIASES[f]) { const cand = p.get(a); if (cand) { v = cand; break; } }
      }
      if (!v && f === "landing_variant_id") v = p.get("variant");
      if (!v && f === "campaign_id") v = p.get("campaign");
      if (v) incoming[f] = String(v).slice(0, 256);
    });
    // First-touch wins, EXCEPT a fresh ad click (gclid/gbraid/wbraid) refreshes it.
    const freshAdClick = incoming.gclid || incoming.gbraid || incoming.wbraid;
    const merged = (existing && !freshAdClick) ? { ...incoming, ...existing } : { ...existing, ...incoming };
    if (!merged.referrer) {
      merged.referrer = (document.referrer || "").slice(0, 512) || undefined;
    }
    if (!merged.first_seen_at) merged.first_seen_at = new Date().toISOString();
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(merged));
    dbg("attribution", merged);
    return merged;
  } catch { return {}; }
}

export function getAttribution() {
  if (!isMeasurementEnabled()) { return {}; }
  try { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || "{}"); }
  catch { return {}; }
}

// Stable anonymous id for stitching the journey before a customer is known.
// P1.4B.2 — canonical anonymous journey identity (D-B2-01/§6). Reuses the
// pre-existing `ua_anon_id` localStorage key rather than introducing a
// second parallel identity. Governed lifecycle metadata lives beside it in
// `ua_anon_meta` — used only to detect rotation cues the SERVER has issued
// via the heartbeat endpoint (which enforces the owner's 180d inactivity /
// 365d absolute policy). The client never invents its own expiry.
function _readAnonMeta() {
  try { return JSON.parse(localStorage.getItem("ua_anon_meta") || "{}"); }
  catch { return {}; }
}
function _writeAnonMeta(meta) {
  try { localStorage.setItem("ua_anon_meta", JSON.stringify(meta || {})); }
  catch { /* noop */ }
}
function _mintAnonId() {
  return "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function getAnonId() {
  // §7 rule 1 — while OFF, do not create or reuse ua_anon_id.
  if (!isMeasurementEnabled()) { return null; }
  try {
    let id = localStorage.getItem("ua_anon_id");
    if (!id) {
      id = _mintAnonId();
      localStorage.setItem("ua_anon_id", id);
      _writeAnonMeta({ first_seen_at_client: new Date().toISOString() });
    }
    return id;
  } catch { return null; }
}

// P1.4B.2 — Rotate the active anonymous identity on the server's cue.
// Historical funnel events are NEVER rewritten (R-B2-11/15). Old ID is
// forgotten by the browser (a new journey starts) but survives in the
// server's `funnel_events` and `identity_resolutions` records.
function _rotateAnonId(reason) {
  try {
    const nextId = _mintAnonId();
    const prev = localStorage.getItem("ua_anon_id");
    localStorage.setItem("ua_anon_id", nextId);
    _writeAnonMeta({
      first_seen_at_client: new Date().toISOString(),
      rotated_from: prev || null,
      rotation_reason: reason || "server_cued",
    });
    // A new anonymous journey also starts a fresh session so the join
    // model (T-B2-04) treats it as a truly new browsing episode.
    try { sessionStorage.removeItem("ua_session_id"); } catch { /* noop */ }
    return nextId;
  } catch { return null; }
}

// P1.4B.2 — server heartbeat. Best-effort. Non-blocking. Never throws.
// Called ONCE per browser session (guarded by a sessionStorage flag) so
// the server can enforce owner policy without spamming.
let _heartbeatFired = false;
export async function anonymousJourneyHeartbeat() {
  if (_heartbeatFired) return;
  if (!isMeasurementEnabled()) return; // §7 — no server-side anonymous journey while OFF.
  _heartbeatFired = true;
  try {
    const anon = getAnonId();
    if (!anon) return;
    const r = await fetch(`${BACKEND_URL}/api/identity/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ anonymous_id: anon, is_test: isTestTraffic() }),
      keepalive: true,
    });
    if (!r.ok) return;
    const j = await r.json().catch(() => null);
    if (j && j.ok && j.rotate) {
      _rotateAnonId(j.reason || "server_cued");
    }
  } catch { /* noop */ }
}

function getSessionId() {
  if (!isMeasurementEnabled()) { return null; }
  try {
    let id = sessionStorage.getItem("ua_session_id");
    if (!id) { id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("ua_session_id", id); }
    return id;
  } catch { return null; }
}

/** Test traffic flag — set by ?test=1 or stored campaign test context.
 *  Once ?test=1 is seen it is persisted for the session, so SUBSEQUENT SPA
 *  pageviews/events (which drop the query string) stay flagged is_test=true. */
export function isTestTraffic() {
  try {
    const url = new URL(window.location.href);
    if (["1", "true"].includes((url.searchParams.get("test") || "").toLowerCase())) {
      try { sessionStorage.setItem("ua_is_test", "true"); } catch { /* noop */ }
      return true;
    }
    return sessionStorage.getItem("ua_is_test") === "true";
  } catch { return false; }
}

// ── first-party funnel logging (always on, non-blocking) ──────────────────
function logInternal(eventType, meta) {
  // §7 rule 4 — while OFF, stop optional anonymous funnel/journey
  // measurement for subsequent browsing. Customer actions (booking,
  // text, call) STILL work — they just are not funnel-logged from the
  // client. Legitimate business records created by those actions live
  // in their own domain endpoints, untouched by this gate.
  if (!isMeasurementEnabled()) { return; }
  try {
    const attr = getAttribution();
    const body = {
      event_type: eventType,
      session_id: getSessionId(),
      anonymous_id: getAnonId(),
      customer_id: meta?.customer_id || null,
      booking_id: meta?.booking_id || null,
      party_id: meta?.party_id || null,
      campaign_id: attr.campaign_id || meta?.campaign_id || null,
      landing_variant_id: attr.landing_variant_id || meta?.landing_variant_id || null,
      recipient_token: attr.recipient_token || null,
      gclid: attr.gclid || null,
      gbraid: attr.gbraid || null,
      wbraid: attr.wbraid || null,
      utm_source: attr.utm_source || null,
      utm_medium: attr.utm_medium || null,
      utm_campaign: attr.utm_campaign || null,
      utm_content: attr.utm_content || null,
      utm_term: attr.utm_term || null,
      referrer: attr.referrer || null,
      route: (typeof window !== "undefined" ? window.location.pathname : null),
      is_test: isTestTraffic(),
      meta: sanitizeMeta(meta),
    };
    fetch(`${BACKEND_URL}/api/funnel/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throw */ }
}

function sanitizeMeta(meta) {
  if (!meta || typeof meta !== "object") return {};
  const out = {};
  // strip the linkage keys we already promote to top-level
  const skip = new Set(["customer_id", "booking_id", "party_id", "campaign_id", "landing_variant_id"]);
  Object.keys(meta).slice(0, 25).forEach((k) => {
    if (skip.has(k)) return;
    const v = meta[k];
    if (["string", "number", "boolean"].includes(typeof v) || v === null) out[k] = v;
  });
  return out;
}

// ── public API ────────────────────────────────────────────────────────────
/** A pageview/screen view — called on every SPA route change. */
export function trackPageview(path, meta = {}) {
  if (!isMeasurementEnabled()) return;
  try {
    const c = ANALYTICS_CONFIG;
    if (window.gtag && c.ga4) {
      window.gtag("event", "page_view", { page_path: path, page_location: window.location.href, page_title: document.title });
    }
    dbg("pageview", path);
    logInternal("page_view", { ...meta, path });
  } catch { /* noop */ }
}

/** A GA4 + first-party funnel event (not necessarily a conversion). */
export function track(eventName, meta = {}) {
  if (!isMeasurementEnabled()) return;
  try {
    const c = ANALYTICS_CONFIG;
    if (window.gtag && c.ga4) window.gtag("event", eventName, sanitizeMeta(meta));
    dbg("event", eventName, meta);
    logInternal(eventName, meta);
  } catch { /* noop */ }
}

/**
 * A conversion. Fires the Google Ads conversion (if ID+label configured), a GA4
 * event, and the first-party log. kind ∈ "completed_booking" | "click_to_call"
 * | "booking_started".
 */
export function trackConversion(kind, meta = {}) {
  if (!isMeasurementEnabled()) return;
  // §14 — Test/rehearsal/synthetic traffic MUST NOT emit a Production
  // Google conversion (Ads OR GA4). Reuses the already-accepted
  // `isTestTraffic()` classification (?test=1 + sessionStorage sticky).
  // First-party funnel logging still fires below with `is_test: true`
  // so the Owner can still see the test booking in the funnel.
  const testTraffic = isTestTraffic();
  try {
    const c = ANALYTICS_CONFIG;
    const labelMap = {
      completed_booking: c.labelBooking,
      click_to_call: c.labelCall,
      booking_started: c.labelBookingStarted,
    };
    const label = labelMap[kind];
    if (testTraffic) {
      dbg(`Google conversion "${kind}" suppressed — test traffic (?test=1).`);
    } else if (window.gtag && c.adsId && label) {
      const params = { send_to: `${c.adsId}/${label}` };
      if (typeof meta.booked_value === "number") { params.value = meta.booked_value; params.currency = "USD"; }
      if (meta.transaction_id) params.transaction_id = meta.transaction_id;
      window.gtag("event", "conversion", params);
      dbg("ADS conversion", kind, params);
    } else if (window.gtag && c.adsId && !label) {
      dbg(`Ads conversion "${kind}" skipped — no label configured.`);
    }
    // GA4 event mirrors the Google-Production layer — suppressed for test
    // traffic for the same reason as the Ads conversion. First-party
    // funnel record (with is_test flag) is still written below.
    if (!testTraffic && window.gtag && c.ga4) window.gtag("event", kind, sanitizeMeta(meta));
    logInternal(kind, meta);
  } catch { /* noop */ }
}

// ── P1.4B.1 · Paid Acquisition Handoff Continuity ─────────────────────────
// Durable Text/Call CTA-Exit events captured BEFORE native SMS/dialer handoff.
// For Text intent, requests a server-issued opaque acquisition token so the
// eventual trusted inbound SMS can be bridged back to THIS acquisition event.
//
// Analytics MUST NEVER block contact (R-B1-19/39). Every path returns
// promptly; the caller should default to native behavior on failure/timeout.

/** Payload builder — same whitelist as backend CtaExitBody. Never fabricates values. */
function buildCtaExitPayload(kind, surface, meta) {
  const attr = getAttribution();
  const out = {
    kind,
    surface: surface || undefined,
    session_id: getSessionId() || undefined,
    anonymous_id: getAnonId() || undefined,
    campaign_id: attr.campaign_id || meta?.campaign_id || undefined,
    landing_variant_id: attr.landing_variant_id || meta?.landing_variant_id || undefined,
    recipient_token: attr.recipient_token || undefined,
    gclid: attr.gclid || undefined,
    gbraid: attr.gbraid || undefined,
    wbraid: attr.wbraid || undefined,
    utm_source: attr.utm_source || undefined,
    utm_medium: attr.utm_medium || undefined,
    utm_campaign: attr.utm_campaign || undefined,
    utm_content: attr.utm_content || undefined,
    utm_term: attr.utm_term || undefined,
    google_campaignid: attr.google_campaignid || undefined,
    google_adgroupid: attr.google_adgroupid || undefined,
    google_creative: attr.google_creative || undefined,
    google_keyword: attr.google_keyword || undefined,
    google_targetid: attr.google_targetid || undefined,
    google_matchtype: attr.google_matchtype || undefined,
    google_network: attr.google_network || undefined,
    google_device: attr.google_device || undefined,
    google_loc_physical_ms: attr.google_loc_physical_ms || undefined,
    google_loc_interest_ms: attr.google_loc_interest_ms || undefined,
    referrer: attr.referrer || undefined,
    route: (typeof window !== "undefined" ? window.location.pathname : undefined),
    offer_context: meta?.offer_context || undefined,
    is_test: isTestTraffic(),
  };
  return out;
}

/**
 * Fire the durable Text CTA-Exit event AND request the opaque reference
 * token. Returns Promise<{ ok: boolean, ref_token: string|null }>.
 *
 * Contract:
 *   • Resolves within `timeoutMs` (default 800ms) with { ok: false, ref_token: null }
 *     if the server is slow — the caller MUST then still open native SMS
 *     (R-B1-19). No analytics failure UI. No fabricated token.
 *   • Never throws.
 */
export function trackTextCtaExit(surface, meta = {}, timeoutMs = 800) {
  // §7 rule 5 — while OFF, do not issue a server-side acquisition token
  // that would let a later inbound SMS be joined to the opted-out
  // journey. Resolve immediately so native SMS still opens (§9 script
  // blocked / §5 preserve customer actions). No fabricated tokens.
  if (!isMeasurementEnabled()) {
    return Promise.resolve({ ok: false, ref_token: null });
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = (payload) => {
      if (done) return;
      done = true;
      resolve(payload);
    };
    try {
      const body = buildCtaExitPayload("text", surface, meta);
      // Also emit into the GA4/Ads layer so existing dashboards keep counting
      // Text exits when a label is configured.
      try {
        if (window.gtag && ANALYTICS_CONFIG.ga4) {
          window.gtag("event", "text_cta_exit", sanitizeMeta({ ...meta, surface }));
        }
      } catch { /* noop */ }
      // Race the fetch against a short timeout — contact must not be blocked.
      const t = setTimeout(() => finish({ ok: false, ref_token: null }), timeoutMs);
      fetch(`${BACKEND_URL}/api/funnel/cta-exit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { clearTimeout(t); finish({ ok: !!(j && j.ok), ref_token: (j && j.ref_token) || null }); })
        .catch(() => { clearTimeout(t); finish({ ok: false, ref_token: null }); });
    } catch { finish({ ok: false, ref_token: null }); }
  });
}

/**
 * Fire the durable Call CTA-Exit event (best-effort, non-blocking). Uses
 * keepalive so the record survives the navigation to `tel:` on mobile. The
 * caller should NOT await this — native dial must proceed immediately (R-B1-39).
 * Also emits the legacy `click_to_call` GAds conversion path so existing
 * dashboards remain intact.
 */
export function trackCallCtaExit(surface, meta = {}) {
  // §5 preserve customer actions — the `tel:` navigation happens
  // independently of this function. While OFF, we silently no-op the
  // measurement side without disturbing the caller.
  if (!isMeasurementEnabled()) { return; }
  try {
    const body = buildCtaExitPayload("call", surface, meta);
    try {
      if (window.gtag && ANALYTICS_CONFIG.ga4) {
        window.gtag("event", "call_cta_exit", sanitizeMeta({ ...meta, surface }));
      }
    } catch { /* noop */ }
    // Existing Google Ads click_to_call conversion — preserved for continuity
    // with the existing measurement setup. This is the classic GAds signal
    // and remains distinct from the durable first-party call_cta_exit event.
    try { trackConversion("click_to_call", { ...meta, surface }); } catch { /* noop */ }
    fetch(`${BACKEND_URL}/api/funnel/cta-exit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throw — Call must remain functional. */ }
}
